import { Control, Controller } from 'react-hook-form';
import { Typography, Button, Autocomplete, TextField, CircularProgress, Paper, Tooltip } from '@mui/material';
import { FaChevronRight } from 'react-icons/fa';
import React, { useState, useMemo, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { useGetBusinessAutocomplete, useGetBusinessPlaceDetails } from 'apis/agency/businessOnboarding/hooks';
import { BusinessPrediction } from 'apis/agency/businessOnboarding/types';
import lookup from 'tz-lookup';

interface StepProps {
  onNext: () => void | Promise<void>;
  control: Control<any>;
  setValue: any;
  isSubmitting?: boolean;
}

const BusinessNameStep: React.FC<StepProps> = ({ onNext, control, setValue, isSubmitting = false }) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Debounce input to avoid excessive API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(inputValue);
    }, 500);
    return () => clearTimeout(handler);
  }, [inputValue]);

  const { data: autocompleteData, isFetching: loading } = useGetBusinessAutocomplete(debouncedInput);
  const { mutateAsync: fetchPlaceDetails, isPending: isFetchingDetails } = useGetBusinessPlaceDetails();

  const options = useMemo(() => {
    const list = autocompleteData || [];
    return list.map((p: BusinessPrediction) => ({
      label: p.structured_formatting?.main_text || p.description,
      value: p.structured_formatting?.main_text || p.description,
      placeId: p.place_id,
      terms: p.terms
    }));
  }, [autocompleteData]);

  const handleStartNow = async () => {
    onNext();
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3, width: '100%' }}>
      <Typography fontWeight={600} sx={{ fontSize: { xs: '16px', md: '24px' } }}>
        What&apos;s your business name?
      </Typography>

      <Typography lineHeight='normal' fontSize='14px' color='text.secondary' mt={1}>
        We&apos;ll use this to personalise your AI agent&apos;s greeting and call scripts.
      </Typography>

      <Controller
        name='business_legal_name'
        control={control}
        rules={{ required: 'Business name is required' }}
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            size={isMobile ? 'small' : 'medium'}
            freeSolo
            filterOptions={(x) => x}
            options={options}
            loading={loading}
            onInputChange={(_, newValue) => {
              setInputValue(newValue);
              field.onChange(newValue);
            }}
            onChange={async (_, newValue) => {
              const val = typeof newValue === 'string' ? newValue : newValue?.label || '';
              const placeId = typeof newValue === 'string' ? null : newValue?.placeId || null;

              field.onChange(val);

              if (placeId) {
                try {
                  const data = await fetchPlaceDetails(placeId);
                  if (data) {
                    if (data.current_opening_hours?.periods) {
                      setValue('business_hours', data.current_opening_hours.periods);
                    }
                    if (data.website) {
                      setValue('website_url', data.website);
                    }

                    const components = data.address_components || [];
                    const getComponent = (types: string[]) =>
                      components.find((c: any) => types.every((t) => c.types.includes(t)))?.short_name || '';

                    const streetNumber = getComponent(['premise']) || getComponent(['street_number']);
                    const route = getComponent(['route']);
                    const streetAddress = [streetNumber, route].filter(Boolean).join(' ');

                    setValue('address.street_address', data.formatted_address || streetAddress || '');
                    setValue('address.city', getComponent(['locality']));
                    setValue('address.state_province_region', getComponent(['administrative_area_level_1']));
                    setValue('address.postal_zip_code', getComponent(['postal_code']));
                    setValue('address.country', getComponent(['country']));

                    const lat = data.geometry?.location?.lat;
                    const lng = data.geometry?.location?.lng;
                    if (lat && lng) {
                      const timezone = lookup(lat, lng);
                      if (timezone) {
                        setValue('address.time_zone', timezone);
                      }
                    }
                  }
                } catch {
                  console.error('Error fetching business details on change:');
                }
              }
            }}
            value={field.value || ''}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            renderOption={(props, option) => {
              const label = typeof option === 'string' ? option : option.label;
              return (
                <li {...props}>
                  <Typography>{label}</Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <Tooltip
                title={field.value || ''}
                placement='top'
                disableHoverListener={!field.value}
                disableFocusListener
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'common.white',
                      color: 'text.primary',
                      boxShadow: 1,
                      border: '1px solid #E0E0E0',
                      fontSize: 13,
                      borderRadius: 1.5,
                      p: 1
                    }
                  }
                }}
              >
                <TextField
                  {...params}
                  label={
                    <span style={{ display: 'flex', gap: 2 }}>
                      Business Name
                      <Typography component='span' color='#DA0808'>
                        *
                      </Typography>
                    </span>
                  }
                  placeholder='Search Your Business'
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  sx={{ mt: 3 }}
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      sx: {
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }
                    },
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? <CircularProgress color='inherit' size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      )
                    }
                  }}
                />
              </Tooltip>
            )}
          />
        )}
      />

      <Button
        variant='contained'
        fullWidth
        disabled={isFetchingDetails || isSubmitting}
        sx={{
          mt: 3,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.palette.primary.main
        }}
        onClick={handleStartNow}
      >
        {isFetchingDetails || isSubmitting ? (
          <CircularProgress size={20} color='inherit' />
        ) : (
          <>
            Continue <FaChevronRight />
          </>
        )}
      </Button>
    </Paper>
  );
};

export default BusinessNameStep;
